import axios from "axios";
import store from "@/redux/store";
import { logoutVendor, setVendorTokens } from "@/redux/slices/vendorSlice";
import { attachInterceptors } from "./interceptor";
import { createCachedFetcher } from "./requestCache";

const API_BASE_URL = "https://ilosiwaju-mbaay-2025.com/api/v1/vendor";
const CHAT_BASE_URL = "https://ilosiwaju-mbaay-2025.com/api/v1/chat";

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Attach interceptors using the shared utility
attachInterceptors(
  api,
  store,
  (state) => state.vendor.token,
  (state) => state.vendor.refreshToken,
  setVendorTokens,
  logoutVendor,
  `${API_BASE_URL}/refresh_token`,
);

export const createVendor = async (userData: any) => {
  try {
    const response = await api.post("/create_vendor", userData);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Failed to create account",
    );
  }
};

export const LoginVendorAPI = async (userData: any) => {
  try {
    const response = await api.post("/login_vendor", userData);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Check your network connection",
    );
  }
};

// Best-effort server-side vendor logout. Plain axios (not the intercepted
// instance) so an expired access token can't trigger a refresh during logout.
export const logoutVendorApi = async (token?: string | null) => {
  try {
    await axios.post(
      `${API_BASE_URL}/logout`,
      {},
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
    );
  } catch {
    // Ignore — the client clears vendor session regardless.
  }
};

export const get_single_vendor = async (token?: string | null) => {
  try {
    // If token is provided, use it (for manual calls), otherwise interceptor will add it
    const config = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    const response = await api.get("/find_one_vendor", config);
    return response.data.data;
  } catch (error: any) {
    throw error.response?.data?.message || "Check your network connection";
  }
};

export const upload_return_policy = async (
  data: any,
  token?: string | null,
) => {
  try {
    const config = {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": undefined as any, // remove JSON default so multipart boundary is auto-set
      },
    };
    const response = await api.post("/upload_return_policy", data, config);
    return response;
  } catch (error: any) {
    throw error.response?.data?.message || "Check your network connection";
  }
};

// Both exports hit the same /get_all_vendors endpoint; share one cached,
// de-duplicated request so repeated navigations don't refetch the vendor list.
const fetchAllVendorsRaw = async () => {
  try {
    const response = await api.get("/get_all_vendors");
    return response.data;
  } catch (error: any) {
    throw error.response?.data?.message || "Check your network connection";
  }
};

const cachedAllVendors = createCachedFetcher(fetchAllVendorsRaw, 5 * 60 * 1000);

export const getAllVendor = () => cachedAllVendors();

export const getAlllVendor = async () => (await cachedAllVendors()).vendors;

// Verify Google ID token
export const verifyVendorGoogle = async (idToken: string) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/google-verify`,
      { token: idToken },
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Google verification failed",
    );
  }
};

// Complete Google signup
export const completeVendorSignup = async (data: {
  tempToken: string;
  storeName: string;
  craftCategories: string[];
  storePhone: string;
}) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/google-complete`, data, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Signup completion failed",
    );
  }
};

export const getVendorStat = async (token?: string | null) => {
  try {
    const config = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    const response = await api.get("/vendorstats", config);
    return response.data;
  } catch (error: any) {
    throw error.response?.data?.message || "Check your network connection";
  }
};

export const vendorKycUpload = async (data: any, token?: string | null) => {
  try {
    const config = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    const response = await api.post("/upload_kyc", data, config);
    return response.data;
  } catch (error: any) {
    throw error.response?.data?.message || "Check your network connection";
  }
};

const NotBaseUrl = "https://ilosiwaju-mbaay-2025.com/api/v1/notifications";
export const getVendorNotification = async (id: string | null) => {
  try {
    const response = await api.get(`${NotBaseUrl}/allnotifications/${id}`);
    return response.data;
  } catch (error: any) {
    throw error.response?.data?.message || "Check your network connection";
  }
};

export const markOneAsRead = async (
  vendorId: string | null,
  notificationId: string | null,
) => {
  try {
    const response = await api.patch(
      `${NotBaseUrl}/notifications/${notificationId}/${vendorId}`,
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data?.message || "Check your network connection";
  }
};

export const markVendorNotificationAsRead = async (id: string | null) => {
  try {
    const response = await api.patch(
      `${NotBaseUrl}/notifications/read-all/${id}`,
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data?.message || "Check your network connection";
  }
};

// ---- Chat helpers (added per request) ----
export const get_unread_chat_count = async (userId: string) => {
  try {
    const res = await api.get(
      `${CHAT_BASE_URL}/get_unread_chat_count/${userId}`,
    );
    return res.data; // expected { count }
  } catch (error: any) {
    throw error.response?.data?.message || "Check your network connection";
  }
};

export const mark_chat_as_read = async (chatId: string, userId: string) => {
  try {
    const res = await api.patch(
      `${CHAT_BASE_URL}/mark_chat_as_read/${chatId}/${userId}`,
    );
    return res.data;
  } catch (error: any) {
    throw error.response?.data?.message || "Check your network connection";
  }
};

// --- Additional vendor endpoints requested ---
export const changeVendorPassword = async (
  newPassword: string,
  confirmPassword: string,
  token?: string | null,
) => {
  try {
    const config = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    const response = await api.patch(
      "/change_password",
      { newPassword, confirmPassword },
      config,
    );
    return response.data; // expected success message
  } catch (error: any) {
    throw error.response?.data?.message || "Check your network connection";
  }
};

export const updateVendorLocation = async (
  location: {
    country?: string;
    state?: string;
    city?: string;
    address1?: string;
    address2?: string;
    postalCode?: string;
    [key: string]: any;
  },
  token?: string | null,
) => {
  try {
    const config = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    const response = await api.patch("/change_location", location, config);
    return response.data; // expected updated location
  } catch (error: any) {
    throw error.response?.data?.message || "Failed to update location";
  }
};

export const initiateVendorEmailChange = async (
  email: string,
  token?: string | null,
) => {
  try {
    const config = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    const response = await api.patch("/change_email", { email }, config);
    return response.data; // expected message: "Email OTP sent"
  } catch (error: any) {
    throw error.response?.data?.message || "Failed to initiate email change";
  }
};

export const verifyVendorEmail = async (
  email: string,
  otp: string,
  token?: string | null,
) => {
  try {
    const config = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    const response = await api.patch("/verify_email", { email, otp }, config);
    return response.data; // expected message: "Email updated" or similar
  } catch (error: any) {
    throw error.response?.data?.message || "Failed to verify email";
  }
};

export const updateStoreDetails = async (
  details: {
    storeName?: string;
    storePhone?: string;
    businessDescription?: string;
  },
  token?: string | null,
) => {
  try {
    const config = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    const response = await api.patch("/update_vendor_info", details, config);
    return response.data;
  } catch (error: any) {
    throw error.response?.data?.message || "Failed to update store details";
  }
};

// ---- Reviews endpoints used by vendor ----
const REVIEWS_BASE_URL = "https://ilosiwaju-mbaay-2025.com/api/v1/reviews";

/** Reply to a review (public or private reply)
 * PATCH /api/reviews/reply
 * body: { reviewId, message: string, isPublic?: boolean (default true), messageType?: string }
 */
export const replyToReview = async (
  reviewId: string,
  payload: { message: string; isPublic?: boolean; messageType?: string },
  _token: string | null,
) => {
  try {
    const body = { reviewId, ...payload } as Record<string, any>;
    const response = await api.patch(
      `${REVIEWS_BASE_URL}/reply/${reviewId}`,
      body,
    );
    return response.data;
  } catch (error: any) {
    throw (
      error.response?.data?.message ||
      error.message ||
      "Failed to reply to review"
    );
  }
};

/** Send a private review message (vendor <-> customer)
 * POST /api/reviews/private-message
 * body: { reviewId, message, messageType }
 */
export const sendPrivateReviewMessage = async (
  _token: string | null,
  body: { reviewId: string; message: string; messageType?: string },
) => {
  try {
    const response = await api.post(
      `${REVIEWS_BASE_URL}/private-message`,
      body,
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data?.message || "Failed to send private message";
  }
};

/** Get all reviews for vendor's products
 * GET /api/reviews/vendor
 * Query params: page, limit, status
 * Returns: { reviews: [], stats: {}, pagination: {} }
 */
export const getVendorReviews = async (_token: string | null) => {
  try {
    const response = await api.get(`${REVIEWS_BASE_URL}/vendor`);
    return response.data;
  } catch (error: any) {
    throw error.response?.data?.message || "Failed to fetch vendor reviews";
  }
};
